import { OPENAI_API_KEY, OPENAI_MODEL, OPENAI_API_URL } from './apiConfig';

export interface ExtractedInfo {
  sentence: string;
  location: string | null;
  employee_count: number | null;
  measurements: {
    length: number | null;
    area: number | null;
    width: number | null;
    diameter: number | null;
    depth: number | null;
    angle: number | null;
    volume: number | null;
    size: number | null;
    height: number | null;
  };
}

export async function extract_sentence_info(sentence: string): Promise<ExtractedInfo> {
  if (!OPENAI_API_KEY) {
    throw new Error('OpenAI API key not configured. Please set your API key in apiConfig.ts');
  }

  const systemPrompt = [
    'Extract the following fields from the given sentence:',
    '- location: Only the location name (no prepositions, e.g., "Utrecht" not "in Utrecht"). Use null if not present.',
    '- employee_count: Number of employees doing the action (1 for "I", 2+ for "we", count names in "John and I" patterns).',
    '- measurements: For each (length, area, width, diameter, depth, angle, volume, size, height), extract the number only (ignore units). Use null if not present.',
    'Return a single JSON object with these fields. Do NOT include explanations or markdown.',
    '',
    'EXAMPLES:',
    'Input: "I removed a 3m long PVC pipe."',
    'Output:',
    '{',
    '  "sentence": "I removed a 3m long PVC pipe.",',
    '  "location": null,',
    '  "employee_count": 1,',
    '  "measurements": {',
    '    "length": 3,',
    '    "area": null,',
    '    "width": null,',
    '    "diameter": null,',
    '    "depth": null,',
    '    "angle": null,',
    '    "volume": null,',
    '    "size": null,',
    '    "height": null',
    '  }',
    '}',
    '',
    'Input: "We installed a pipe, d=5cm, l=2m in the basement."',
    'Output:',
    '{',
    '  "sentence": "We installed a pipe, d=5cm, l=2m in the basement.",',
    '  "location": "basement",',
    '  "employee_count": 2,',
    '  "measurements": {',
    '    "length": 2,',
    '    "area": null,',
    '    "width": null,',
    '    "diameter": 5,',
    '    "depth": null,',
    '    "angle": null,',
    '    "volume": null,',
    '    "size": null,',
    '    "height": null',
    '  }',
    '}',
    '',
    'Input: "I cleaned the pipe."',
    'Output:',
    '{',
    '  "sentence": "I cleaned the pipe.",',
    '  "location": null,',
    '  "employee_count": 1,',
    '  "measurements": {',
    '    "length": null,',
    '    "area": null,',
    '    "width": null,',
    '    "diameter": null,',
    '    "depth": null,',
    '    "angle": null,',
    '    "volume": null,',
    '    "size": null,',
    '    "height": null',
    '  }',
    '}',
  ].join('\n');

  const userPrompt = `Input: "${sentence}"\nExtract the values as described.`;

  const response = await fetch(OPENAI_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${OPENAI_API_KEY}`,
    },
    body: JSON.stringify({
      model: OPENAI_MODEL,
      messages: [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: userPrompt
        }
      ],
      max_tokens: 200,
      temperature: 0.1,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`OpenAI API error: ${response.status} ${response.statusText} - ${errorData.error?.message || 'Unknown error'}`);
  }

  const data = await response.json();
  const aiResponse = data.choices[0]?.message?.content?.trim();

  if (!aiResponse) {
    throw new Error('No response received from OpenAI API');
  }

  // Remove markdown code block markers if present
  let cleaned = aiResponse.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/```[a-zA-Z]*|```/g, '').trim();
  }

  let parsedResponse;
  try {
    parsedResponse = JSON.parse(cleaned);
  } catch (jsonError) {
    console.error('Failed to parse AI response as JSON:', jsonError);
    throw new Error('AI did not return valid JSON.');
  }

  return parsedResponse;
} 